import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { appointmentService } from '../../services/appointmentService';
import { Appointment } from '../../types/appointment.types';
import AppointmentManageModal from '../../components/client/AppointmentManageModal';
import logo from '../../assets/logo-lnv.jpeg';
import './ClientDashboard.css';

const ClientDashboard: React.FC = () => {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
    const [showManageModal, setShowManageModal] = useState(false);
    const navigate = useNavigate();
    const user = authService.getCurrentUser();

    useEffect(() => {
        loadAppointments();
    }, []);

    const loadAppointments = async () => {
        try {
            const data = await appointmentService.getMyAppointments();
            setAppointments(data);
        } catch (error) {
            console.error('Error loading appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        authService.logout();
        navigate('/login');
    };

    const canManageAppointment = (appointment: Appointment): boolean => {
        return appointment.status === 'CONFIRMED' || appointment.status === 'PENDING';
    };

    const handleAppointmentClick = (appointment: Appointment) => {
        if (canManageAppointment(appointment)) {
            setSelectedAppointment(appointment);
            setShowManageModal(true);
        }
    };

    return (
        <div className="dashboard">
            {/* Header */}
            <header className="dashboard-header">
                <div className="container">
                    <div className="header-content">
                        <div className="logo-section">
                            <img src={logo} alt="LNV Logo" style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '50%' }} />
                            <h1 className="logo-title">LNV</h1>
                        </div>
                        <div className="user-section">
                            <span className="user-name">Hola, {user?.firstName || user?.name}!</span>
                            <button className="btn btn-outline btn-sm" onClick={handleLogout}>
                                Cerrar Sesión
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="dashboard-main">
                <div className="container">
                    {/* Compact Header with Stats and Actions */}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        marginBottom: '1.5rem'
                    }}>
                        {/* Quick Actions Row */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.75rem',
                            alignItems: 'center'
                        }}>
                            <button
                                onClick={() => navigate('/client/book-appointment')}
                                className="btn btn-primary"
                                style={{
                                    borderRadius: '2rem',
                                    padding: '0.75rem 1.5rem',
                                }}
                            >
                                <span>📅</span>
                                <span>Nuevo Turno</span>
                            </button>

                            <button
                                onClick={() => navigate('/client/history')}
                                className="btn btn-outline"
                                style={{
                                    borderRadius: '2rem',
                                    padding: '0.75rem 1.5rem',
                                }}
                            >
                                <span>📋</span>
                                <span>Historial</span>
                            </button>

                            {/* Stats Badges */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'rgba(190, 242, 100, 0.1)',
                                borderRadius: '2rem',
                                border: '1px solid var(--accent)',
                                marginLeft: 'auto'
                            }}>
                                <span>📅</span>
                                <span style={{ fontWeight: '700', color: 'var(--accent)' }}>
                                    {appointments.filter(a => a.status === 'CONFIRMED' || a.status === 'PENDING').length}
                                </span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Próximos</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--secondary)',
                                borderRadius: '2rem',
                                border: '1px solid var(--secondary-light)',
                                opacity: 0.8
                            }}>
                                <span>⭐</span>
                                <span style={{ fontWeight: '700', color: 'var(--accent)' }}>0</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Puntos</span>
                            </div>
                        </div>
                    </div>

                    {/* Upcoming Appointments */}
                    <section className="appointments-section">
                        <h3 className="section-subtitle">Mis Turnos</h3>
                        {loading ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>Cargando turnos...</p>
                            </div>
                        ) : appointments.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">📅</div>
                                <h4>No tienes turnos registrados</h4>
                                <p>¡Agenda tu primer turno ahora!</p>
                                <button
                                    className="btn btn-accent"
                                    onClick={() => navigate('/client/book-appointment')}
                                >
                                    Agendar Turno
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-md">
                                {appointments.map((appointment) => (
                                    <div
                                        key={appointment.id}
                                        className="card p-md"
                                        style={{
                                            cursor: canManageAppointment(appointment) ? 'pointer' : 'default',
                                            transition: 'transform 0.2s, box-shadow 0.2s'
                                        }}
                                        onClick={() => handleAppointmentClick(appointment)}
                                        onMouseEnter={(e) => {
                                            if (canManageAppointment(appointment)) {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '';
                                        }}
                                    >
                                        <div className="flex justify-between items-center mb-sm">
                                            <span className={`badge badge-${getStatusColor(appointment.status)}`}>
                                                {getStatusLabel(appointment.status)}
                                            </span>
                                            <span className="text-sm font-bold">{appointment.appointmentDate}</span>
                                        </div>
                                        <div className="mb-sm">
                                            <p className="font-bold text-lg m-0">{appointment.barberName}</p>
                                            <p className="text-secondary text-sm">{appointment.branchName}</p>
                                        </div>
                                        <div className="flex justify-between items-center text-sm border-t pt-2 mt-2">
                                            <span>🕙 {appointment.startTime}</span>
                                            <span className="text-accent font-medium">{appointment.serviceDescription || 'Corte de Pelo'}</span>
                                        </div>
                                        {canManageAppointment(appointment) && (
                                            <div style={{
                                                marginTop: '0.75rem',
                                                paddingTop: '0.75rem',
                                                borderTop: '1px solid var(--secondary)'
                                            }}>
                                                <button
                                                    className="btn btn-sm btn-outline"
                                                    style={{ width: '100%' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAppointmentClick(appointment);
                                                    }}
                                                >
                                                    ⚙️ Gestionar Turno
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </main>

            {/* Appointment Manage Modal */}
            <AppointmentManageModal
                appointment={selectedAppointment}
                isOpen={showManageModal}
                onClose={() => {
                    setShowManageModal(false);
                    setSelectedAppointment(null);
                }}
                onAppointmentUpdated={() => {
                    loadAppointments();
                }}
            />
        </div>
    );
};

const getStatusColor = (status: string): string => {
    switch (status) {
        case 'CONFIRMED':
            return 'success';
        case 'PENDING':
            return 'warning';
        case 'CANCELLED':
            return 'error';
        case 'COMPLETED':
            return 'info';
        default:
            return 'info';
    }
};

const getStatusLabel = (status: string): string => {
    switch (status) {
        case 'CONFIRMED': return 'Confirmado';
        case 'PENDING': return 'Pendiente';
        case 'CANCELLED': return 'Cancelado';
        case 'COMPLETED': return 'Completado';
        case 'NO_SHOW': return 'Ausente';
        default: return status;
    }
}

export default ClientDashboard;
